/**
 * Slim popup header — the one bar every popup in the app draws at its top.
 *
 * ~40px tall: an icon, the title, an optional one-line subtitle, an optional slot for
 * extra controls, and a round close button. Two looks:
 *   - 'band'  — the popup's own coloured gradient with white text. Pass the popup's
 *               colour as `background` (any CSS background) or `className` (Tailwind
 *               gradient classes); each popup keeps the colour it already had.
 *   - 'light' — the neutral grey bar, for popups that were never coloured.
 *
 * The header never scrolls with the body: give the popup panel `flex flex-col` and this
 * is `flex-shrink-0`.
 */
import type { CSSProperties, ReactNode } from 'react';

export interface PopupHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Remix icon class, e.g. "ri-file-add-line". */
  icon?: string;
  onClose?: () => void;
  /** Disable the close button (e.g. while saving). */
  closeDisabled?: boolean;
  tone?: 'band' | 'light';
  /** CSS background for the band, e.g. a linear-gradient. */
  background?: string;
  /** Extra classes on the bar — Tailwind gradient classes for a band go here. */
  className?: string;
  /** Controls rendered between the title and the close button. */
  actions?: ReactNode;
}

export default function PopupHeader({
  title,
  subtitle,
  icon,
  onClose,
  closeDisabled = false,
  tone = 'light',
  background,
  className = '',
  actions,
}: PopupHeaderProps) {
  const band = tone === 'band';
  // A band with neither a background nor gradient classes falls back to the violet the
  // FormModal banner always used.
  const style: CSSProperties | undefined = band
    ? { background: background ?? (className.includes('bg-') ? undefined : 'linear-gradient(120deg, #7c3aed 0%, #4a3aa7 100%)') }
    : undefined;

  return (
    <div
      className={`flex flex-shrink-0 items-center gap-2 px-4 py-2 ${
        band ? 'text-white' : 'bg-gray-50 border-b border-gray-200 dark:bg-gray-800 dark:border-gray-700'
      } ${className}`}
      style={style}
    >
      {icon && (
        <i className={`${icon} flex-shrink-0 text-base ${band ? 'text-white/90' : 'text-gray-500 dark:text-gray-400'}`}></i>
      )}
      <div className="flex min-w-0 flex-1 items-baseline gap-2">
        <h3 className={`truncate text-sm font-semibold tracking-tight ${band ? 'text-white' : 'text-gray-800 dark:text-white'}`}>
          {title}
        </h3>
        {subtitle && (
          <p className={`hidden truncate text-[11px] font-medium sm:block ${band ? 'text-white/75' : 'text-gray-500 dark:text-gray-400'}`}>
            {subtitle}
          </p>
        )}
      </div>
      {actions && <div className="flex flex-shrink-0 items-center gap-1.5">{actions}</div>}
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          disabled={closeDisabled}
          aria-label="Close"
          title="Close"
          className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full transition-colors disabled:opacity-50 ${
            band ? 'text-white/90 hover:bg-white/20 hover:text-white' : 'text-gray-400 hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-gray-700'
          }`}
        >
          <i className="ri-close-line text-lg"></i>
        </button>
      )}
    </div>
  );
}
